import type { InventoryItem } from '../../redux/inventorySlice';
import { findDuplicate, isSameFood } from '../../utils/pantryDuplicates';

/**
 * Why a detected item starts unchecked in the capture review.
 * - 'inventory': the same food is already in the pantry
 * - 'batch': an earlier item in this same photo/receipt is the same food
 */
export type CaptureDuplicateReason = 'inventory' | 'batch';

export interface CaptureReviewFlags {
  isAccepted: boolean;
  isDuplicate: boolean;
  duplicateReason?: CaptureDuplicateReason;
}

/**
 * Decide which detected items are duplicates.
 *
 * A single fridge photo routinely yields both "nectarines" and "nectarines,
 * tranchées" — the same fruit twice — so the batch has to be checked against
 * itself, not just against what is already in the pantry.
 *
 * Duplicates are returned unchecked rather than dropped: the user can always
 * tick one back on if the capture really did find two distinct things.
 */
export function flagCaptureDuplicates(
  detected: Array<{ name: string }>,
  inventory: InventoryItem[]
): CaptureReviewFlags[] {
  const acceptedNames: string[] = [];

  return detected.map(item => {
    if (findDuplicate(item.name, inventory)) {
      return { isAccepted: false, isDuplicate: true, duplicateReason: 'inventory' };
    }

    if (acceptedNames.some(name => isSameFood(name, item.name))) {
      return { isAccepted: false, isDuplicate: true, duplicateReason: 'batch' };
    }

    acceptedNames.push(item.name);
    return { isAccepted: true, isDuplicate: false };
  });
}
